import { FIELD_DELIMITER, standardCollator } from 'js/utils/string';
import standardCohortKeys from 'js/utils/cohort-keys';
import { resolveLiveQuestionType } from 'js/utils/sentiment-question';

export const PARTICIPANT_COHORT_SOURCE = '__PARTICIPANT_COHORT__';
export const NO_BREAKOUT_SOURCE = '__NO_BREAKOUT__';
export const OTHER_LABEL = '(Other)';
export const NONE_LABEL = '(None)';
export const RESERVED_LABELS = [NONE_LABEL, OTHER_LABEL];
export const COMBINED_LABEL = '(Combined)';

export function isNoneCohortKey(key) {
  return key === standardCohortKeys.NULL || key === NONE_LABEL;
}

export function isOtherCohortKey(key) {
  return key === OTHER_LABEL || key === standardCohortKeys.OTHER;
}

export function isReservedCohortKey(key) {
  return isNoneCohortKey(key) || isOtherCohortKey(key);
}

/** Sort rank: Combined → named cohorts → (Other) → (None). */
function cohortSortRank(key) {
  if (key === standardCohortKeys.CUMULATIVE) return 0;
  if (key === OTHER_LABEL || key === standardCohortKeys.OTHER) return 2;
  if (key === standardCohortKeys.NULL || key === NONE_LABEL) return 3;
  return 1;
}

/**
 * Order cohorts: (Combined), A–Z named cohorts (standardCollator), (Other), (None).
 * Items may use `{ value, text }` or `{ key, name }`.
 */
export function compareCohortLegendItems(a, b) {
  const aKey = a?.value ?? a?.key;
  const bKey = b?.value ?? b?.key;
  const rankDiff = cohortSortRank(aKey) - cohortSortRank(bKey);
  if (rankDiff !== 0) {
    return rankDiff;
  }
  const aLabel = a?.text ?? a?.name ?? aKey ?? '';
  const bLabel = b?.text ?? b?.name ?? bKey ?? '';
  return standardCollator.compare(String(aLabel), String(bLabel));
}

export function sortCohortLegendItems(items = []) {
  return [...items].sort(compareCohortLegendItems);
}

// Client mirror of `Waypointr.Assessments.Breakout.eligible_question_types/0`.
export const ELIGIBLE_QUESTION_TYPES = [
  'single-select',
  'multi-select',
  'likert',
  'unscored-likert',
  'sentiment',
];

export function isEligibleBreakoutQuestion(question) {
  const type = question?.type || resolveLiveQuestionType(question);
  return ELIGIBLE_QUESTION_TYPES.includes(type);
}

export function listAssessmentQuestions(assessment) {
  return (assessment?.question_sets ?? []).flatMap((qs) =>
    (qs.questions ?? []).map((q) => ({
      ...q,
      questionSet: qs,
      question_set_key: q.question_set_key ?? qs.key,
    }))
  );
}

export function listEligibleBreakoutQuestions(assessment) {
  return listAssessmentQuestions(assessment).filter(isEligibleBreakoutQuestion);
}

export function findQuestionById(assessment, questionId) {
  const id = Number(questionId);
  return listAssessmentQuestions(assessment).find((q) => Number(q.id) === id);
}

export function findQuestionByKeys(assessment, questionSetKey, questionKey) {
  return listAssessmentQuestions(assessment).find(
    (q) =>
      q.key === questionKey &&
      (q.questionSet?.key === questionSetKey ||
        q.question_set_key === questionSetKey)
  );
}

export function parseResponseValues(response) {
  if (
    !response ||
    response.is_na ||
    response.value == null ||
    response.value === ''
  ) {
    return [];
  }
  return String(response.value)
    .split(FIELD_DELIMITER)
    .filter((v) => v !== '');
}

export function participantCohortVaries(participants = []) {
  const values = new Set(
    participants.map((p) =>
      p.cohort == null ? standardCohortKeys.NULL : p.cohort
    )
  );
  return values.size > 1;
}

export function getAvailableBreakouts(assessment) {
  const configured = (assessment?.breakouts ?? []).map((b, index) => ({
    id: b.id ?? `breakout:${index}`,
    index,
    name: b.name,
    source: 'breakout',
    breakout: b,
  }));

  const available = [...configured];

  if (participantCohortVaries(assessment?.participants)) {
    available.push({
      id: PARTICIPANT_COHORT_SOURCE,
      index: null,
      name: 'Member cohort',
      source: 'participant',
      breakout: null,
    });
  }

  if (available.length > 0) {
    available.push({
      id: NO_BREAKOUT_SOURCE,
      index: null,
      name: NONE_LABEL,
      source: 'none',
      breakout: null,
    });
  }

  return available;
}

export function getDefaultBreakoutSelection(assessment) {
  const available = getAvailableBreakouts(assessment);
  const firstConfigured = available.find((b) => b.source === 'breakout');
  if (firstConfigured) {
    return firstConfigured;
  }
  const participantSource = available.find((b) => b.source === 'participant');
  if (participantSource) {
    return participantSource;
  }
  return null;
}

export function unionBreakoutKey(breakout) {
  if (!breakout) {
    return NO_BREAKOUT_SOURCE;
  }
  if (
    breakout.source === 'participant' ||
    breakout.id === PARTICIPANT_COHORT_SOURCE
  ) {
    return PARTICIPANT_COHORT_SOURCE;
  }
  if (breakout.source === 'none' || breakout.id === NO_BREAKOUT_SOURCE) {
    return NO_BREAKOUT_SOURCE;
  }
  return `name:${breakout.name ?? ''}`;
}

/** Stable breakout list across runs: match by id for built-ins, by name otherwise. */
export function unionAssessmentBreakouts(assessments = []) {
  const byKey = new Map();
  assessments.forEach((assessment) => {
    getAvailableBreakouts(assessment).forEach((breakout) => {
      const key = unionBreakoutKey(breakout);
      if (!byKey.has(key)) {
        byKey.set(key, { ...breakout, unionKey: key });
      }
    });
  });
  return [...byKey.values()];
}

export function matchBreakoutOnAssessment(assessment, selected) {
  if (!selected || selected.source === 'none') {
    return (
      getAvailableBreakouts(assessment).find((b) => b.source === 'none') ?? null
    );
  }
  const available = getAvailableBreakouts(assessment);
  if (selected.source === 'participant') {
    return available.find((b) => b.source === 'participant') ?? null;
  }
  return (
    available.find(
      (b) => b.source === 'breakout' && String(b.id) === String(selected.id)
    ) ||
    available.find(
      (b) => b.source === 'breakout' && b.name === selected.name
    ) ||
    null
  );
}

function responseForQuestion(participantId, question) {
  return (question?.responses ?? []).find(
    (r) => String(r.participant_id) === String(participantId)
  );
}

function answerTextByValue(question) {
  return (question?.answers ?? []).reduce((acc, a) => {
    if (a.is_na) {
      return acc;
    }
    acc[String(a.value)] = a.text;
    return acc;
  }, {});
}

function membershipsForEachAnswer(values, question) {
  const labelsByValue = answerTextByValue(question);
  const labels = [];
  values.forEach((value) => {
    const label = labelsByValue[String(value)] ?? String(value);
    if (!labels.includes(label)) {
      labels.push(label);
    }
  });
  return labels;
}

function membershipsForCustom(values, groups = {}) {
  const labels = [];
  let hasUnmapped = false;
  const mappedValues = new Set(
    Object.values(groups).flatMap((vals) => (vals ?? []).map(String))
  );

  values.forEach((value) => {
    const valueKey = String(value);
    Object.entries(groups).forEach(([label, groupValues]) => {
      if (
        (groupValues ?? []).map(String).includes(valueKey) &&
        !labels.includes(label)
      ) {
        labels.push(label);
      }
    });
    if (!mappedValues.has(valueKey)) {
      hasUnmapped = true;
    }
  });

  if (hasUnmapped && !labels.includes(OTHER_LABEL)) {
    labels.push(OTHER_LABEL);
  }

  return labels;
}

function membershipsFromBreakout(participant, breakout, assessment) {
  const question =
    findQuestionByKeys(
      assessment,
      breakout?.question_set_key,
      breakout?.question_key
    ) || findQuestionById(assessment, breakout?.question_ids?.[0]);
  if (!question) {
    return [standardCohortKeys.NULL];
  }

  const response = responseForQuestion(participant.id, question);
  const values = parseResponseValues(response);

  if (values.length === 0) {
    return [standardCohortKeys.NULL];
  }

  if (breakout.grouping_mode === 'custom') {
    const labels = membershipsForCustom(values, breakout.groups || {});
    return labels.length > 0 ? labels : [standardCohortKeys.NULL];
  }

  const labels = membershipsForEachAnswer(values, question);
  return labels.length > 0 ? labels : [standardCohortKeys.NULL];
}

function membershipsFromParticipantCohort(participant) {
  if (participant.cohort == null) {
    return [standardCohortKeys.NULL];
  }
  return [participant.cohort];
}

function cohortDisplay(value) {
  if (value === standardCohortKeys.NULL) {
    return { value: standardCohortKeys.NULL, text: NONE_LABEL };
  }
  if (value === OTHER_LABEL) {
    return { value: OTHER_LABEL, text: OTHER_LABEL };
  }
  return { value, text: value };
}

/**
 * Catalog of cohorts a breakout defines, including groups with no members.
 * Member-cohort source has no catalog beyond observed values.
 */
export function definedBreakoutCohortLabels(assessment, selectedBreakout) {
  if (
    !selectedBreakout ||
    selectedBreakout.source === 'none' ||
    selectedBreakout.source === 'participant'
  ) {
    return [];
  }

  const breakout = selectedBreakout.breakout;
  const question =
    findQuestionByKeys(
      assessment,
      breakout?.question_set_key,
      breakout?.question_key
    ) || findQuestionById(assessment, breakout?.question_ids?.[0]);
  if (!question) {
    return [];
  }

  if (breakout.grouping_mode === 'custom') {
    const labels = Object.keys(breakout.groups || {});
    if (unmappedAnswerValues(question, breakout.groups || {}).length > 0) {
      labels.push(OTHER_LABEL);
    }
    return labels;
  }

  return (question.answers ?? []).filter((a) => !a.is_na).map((a) => a.text);
}

/**
 * Resolve cohorts for an assessment under a selected breakout source.
 * @returns {{
 *   availableBreakouts: array,
 *   selectedBreakout: object|null,
 *   cohorts: array,
 *   participantCohortsMap: object,
 * }}
 */
export function resolveAssessmentCohorts(assessment, selection = null) {
  const availableBreakouts = getAvailableBreakouts(assessment);
  const selectedBreakout =
    selection == null
      ? getDefaultBreakoutSelection(assessment)
      : availableBreakouts.find((b) => b.id === selection) ??
        getDefaultBreakoutSelection(assessment);

  const participants = assessment?.participants ?? [];
  const participantCohortsMap = {};
  const used = new Set();

  if (!selectedBreakout || selectedBreakout.source === 'none') {
    participants.forEach((participant) => {
      participantCohortsMap[participant.id] = [standardCohortKeys.NULL];
    });

    return {
      availableBreakouts,
      selectedBreakout,
      cohorts: [],
      participantCohortsMap,
    };
  }

  participants.forEach((participant) => {
    let labels;
    if (selectedBreakout.source === 'participant') {
      labels = membershipsFromParticipantCohort(participant);
    } else {
      labels = membershipsFromBreakout(
        participant,
        selectedBreakout.breakout,
        assessment
      );
    }
    participantCohortsMap[participant.id] = labels;
    labels.forEach((label) => used.add(label));
  });

  const memberCounts = new Map();
  Object.values(participantCohortsMap).forEach((labels) => {
    labels.forEach((label) => {
      memberCounts.set(label, (memberCounts.get(label) ?? 0) + 1);
    });
  });

  const defined = definedBreakoutCohortLabels(assessment, selectedBreakout);
  const allValues = new Set([...defined, ...used]);
  const cohorts = sortCohortLegendItems(
    Array.from(allValues).map((value) => ({
      ...cohortDisplay(value),
      memberCount: memberCounts.get(value) ?? 0,
      empty: (memberCounts.get(value) ?? 0) === 0,
    }))
  ).filter((cohort) => !cohort.empty || !isReservedCohortKey(cohort.value));

  return {
    availableBreakouts,
    selectedBreakout,
    cohorts,
    participantCohortsMap,
  };
}

/** Normalize map entries to string[] (supports legacy single-string maps). */
export function getParticipantCohorts(participantCohortsMap, participantId) {
  const raw = participantCohortsMap?.[participantId];
  if (raw == null) {
    return [standardCohortKeys.NULL];
  }
  if (Array.isArray(raw)) {
    return raw.length > 0 ? raw : [standardCohortKeys.NULL];
  }
  return [raw];
}

export function participantMatchesCohortFilter(
  participantCohortsMap,
  participantId,
  filter
) {
  const cohortFilter = filter?.cohorts ?? {};
  const filterKeys = Object.keys(cohortFilter);
  if (filterKeys.length === 0) {
    return true;
  }
  const labels = getParticipantCohorts(participantCohortsMap, participantId);
  return labels.some((label) => cohortFilter[label]);
}

export function unmappedAnswerValues(question, groups = {}) {
  const mapped = new Set(
    Object.values(groups).flatMap((vals) => (vals ?? []).map(String))
  );
  return (question?.answers ?? [])
    .filter((a) => !a.is_na)
    .filter((a) => !mapped.has(String(a.value)));
}
